/** A point on the horizontal plane, as world [x, z] */
export type PlanarPoint = [x: number, z: number];

/** World placement of the map: where the project coordinates lie and how the map is rotated around Y */
export type WorldMapPose = {
  x: number;
  z: number;
  /** Rotation around the world Y axis, in radians (three.js convention) */
  rotation: number;
};

function rotate([x, z]: PlanarPoint, angle: number): PlanarPoint {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return [x * cos + z * sin, -x * sin + z * cos];
}

function directionAngle(from: PlanarPoint, to: PlanarPoint): number {
  return Math.atan2(-(to[1] - from[1]), to[0] - from[0]);
}

function midpoint(a: PlanarPoint, b: PlanarPoint): PlanarPoint {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

/**
 * @param pose The current map placement
 * @param point A world point on the map
 * @returns The point in the map's own coordinates
 */
export function toMapLocal(
  pose: WorldMapPose,
  point: PlanarPoint,
): PlanarPoint {
  return rotate([point[0] - pose.x, point[1] - pose.z], -pose.rotation);
}

/**
 * Computes the rigid placement of the map so that two map points land on two data points.
 * The map keeps its real-world scale, so only position and rotation change.
 *
 * @param pose The current map placement, used to read the picked map points in map coordinates
 * @param dataPoints Two world points picked on the user's data
 * @param mapPoints The two world points picked on the map that correspond to the data points
 * @returns The new map placement
 */
export function computeAlignedPose(
  pose: WorldMapPose,
  dataPoints: [PlanarPoint, PlanarPoint],
  mapPoints: [PlanarPoint, PlanarPoint],
): WorldMapPose {
  const local1 = toMapLocal(pose, mapPoints[0]);
  const local2 = toMapLocal(pose, mapPoints[1]);
  const rotation =
    directionAngle(dataPoints[0], dataPoints[1]) -
    directionAngle(local1, local2);
  const [dataX, dataZ] = midpoint(dataPoints[0], dataPoints[1]);
  const [mapX, mapZ] = rotate(midpoint(local1, local2), rotation);
  return { x: dataX - mapX, z: dataZ - mapZ, rotation };
}
